import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "../../components/common/Button";
import { InfoBar } from "../../components/common/InfoBar";
import kybTvLogo from "../../assets/logos/kyb-tv-logo.svg";

type CooperationModelData = {
  name?: string;
  description?: string;
};

type CooperationModelRecord = {
  id: number | string;
  created_at?: string;
  model_data?: CooperationModelData | null;
};

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL?.replace(/\/$/, "");
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const extractRatioWeight = (value?: string) => {
  if (!value) return null;

  const match = value.match(/(\d+)\s*\/\s*(\d+)/);

  if (!match) return null;

  const left = Number(match[1]);
  const right = Number(match[2]);

  if (!Number.isFinite(left) || !Number.isFinite(right)) return null;

  return left - right;
};

const normalizeDescription = (description?: string) => {
  if (!description) return [];

  if (/<\/?[a-z][\s\S]*>/i.test(description)) {
    return [];
  }

  return description
    .split(/\r?\n|[•●▪]/g)
    .map((item) => item.replace(/^\s*[-–—]\s*/, "").trim())
    .filter(Boolean);
};

const sortModels = (records: CooperationModelRecord[]) => {
  const allHaveRatios = records.every((record) =>
    Boolean(extractRatioWeight(record?.model_data?.name)),
  );

  if (allHaveRatios) {
    return [...records].sort((a, b) => {
      const aWeight = extractRatioWeight(a?.model_data?.name) ?? 0;
      const bWeight = extractRatioWeight(b?.model_data?.name) ?? 0;

      return bWeight - aWeight;
    });
  }

  return [...records].sort((a, b) => {
    const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;

    return aDate - bDate;
  });
};

const BRAND_TONES = [
  {
    surface:
      "bg-[radial-gradient(circle_at_top,rgba(126,147,220,0.26),transparent_34%),linear-gradient(180deg,rgba(255,255,255,0.018)_0%,rgba(255,255,255,0.004)_100%)]",
    line: "bg-[linear-gradient(90deg,#dce4fb_0%,rgba(220,228,251,0.05)_100%)]",
    number: "text-[rgba(220,228,251,0.08)]",
    accent: "text-[#9badde]",
    accentHex: "#9badde",
  },
  {
    surface:
      "bg-[radial-gradient(circle_at_top,rgba(93,173,191,0.24),transparent_34%),linear-gradient(180deg,rgba(255,255,255,0.018)_0%,rgba(255,255,255,0.004)_100%)]",
    line: "bg-[linear-gradient(90deg,#b8edf4_0%,rgba(184,237,244,0.05)_100%)]",
    number: "text-[rgba(184,237,244,0.08)]",
    accent: "text-[#7fd1df]",
    accentHex: "#7fd1df",
  },
  {
    surface:
      "bg-[radial-gradient(circle_at_top,rgba(189,155,106,0.2),transparent_34%),linear-gradient(180deg,rgba(255,255,255,0.018)_0%,rgba(255,255,255,0.004)_100%)]",
    line: "bg-[linear-gradient(90deg,#f2d4a1_0%,rgba(242,212,161,0.05)_100%)]",
    number: "text-[rgba(242,212,161,0.08)]",
    accent: "text-[#d9b16f]",
    accentHex: "#d9b16f",
  },
];

export const CooperationModels = () => {
  const [models, setModels] = useState<CooperationModelRecord[]>([]);
  const [pending, setPending] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [viewport, setViewport] = useState({
    innerWidth: 0,
    innerHeight: 0,
  });

  const hasSupabaseConfig = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

  const fetchModels = useCallback(async () => {
    if (!hasSupabaseConfig) {
      setPending(false);
      setErrorMessage(null);
      setModels([]);
      return;
    }

    setPending(true);
    setErrorMessage(null);

    try {
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/cooperation_models?select=id,created_at,model_data`,
        {
          headers: {
            apikey: SUPABASE_ANON_KEY as string,
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Не удалось загрузить модели сотрудничества.");
      }

      const payload = (await response.json()) as CooperationModelRecord[];
      setModels(sortModels(payload ?? []));
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Не удалось загрузить модели сотрудничества.",
      );
      setModels([]);
    } finally {
      setPending(false);
    }
  }, [hasSupabaseConfig]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  useEffect(() => {
    const updateViewport = () => {
      setViewport({
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
      });
    };

    updateViewport();
    window.addEventListener("resize", updateViewport);

    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  const normalizedModels = useMemo(
    () =>
      models
        .map((model) => ({
          ...model,
          model_data: {
            name: model?.model_data?.name?.trim() || "Без названия",
            description: model?.model_data?.description?.trim() || "",
          },
        }))
        .filter((model) => model.model_data.name || model.model_data.description),
    [models],
  );

  const isCompactTvViewport =
    viewport.innerWidth > 0 &&
    viewport.innerWidth <= 1100 &&
    viewport.innerHeight > 0 &&
    viewport.innerHeight <= 560;
  const compactCanvasWidth = 1180;
  const compactCanvasHeight = 620;
  const compactScale = isCompactTvViewport
    ? Math.max(
        0.72,
        Math.min(
          0.95,
          (viewport.innerWidth - 24) / compactCanvasWidth,
          (viewport.innerHeight - 24) / compactCanvasHeight,
        ),
      )
    : 1;

  return (
    <div className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top_left,rgba(91,110,170,0.18),transparent_18%),radial-gradient(circle_at_bottom_right,rgba(171,132,84,0.12),transparent_18%),linear-gradient(180deg,#02040e_0%,#050816_52%,#080d1c_100%)] px-5 pb-8 pt-5 text-[#f5f7ff] before:pointer-events-none before:absolute before:inset-0 before:bg-[linear-gradient(rgba(255,255,255,0.018)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.018)_1px,transparent_1px)] before:bg-[size:72px_72px] before:[mask-image:linear-gradient(180deg,rgba(0,0,0,0.45),transparent_82%)] md:h-[100dvh] md:px-4 md:pb-3 md:pt-3 md:overflow-hidden xl:px-6 xl:pb-3 xl:pt-3 2xl:px-8 2xl:pb-4 2xl:pt-4">
      <div className="pointer-events-none absolute -left-[120px] top-[-100px] h-[420px] w-[420px] rounded-full bg-[rgba(99,113,168,0.24)] opacity-40 blur-[90px]" />
      <div className="pointer-events-none absolute right-[-160px] top-20 h-[420px] w-[420px] rounded-full bg-[rgba(142,160,210,0.16)] opacity-40 blur-[90px]" />

      <div className="relative z-[1] mx-auto w-full max-w-[1460px] xl:h-full 2xl:max-w-[1640px]">
        <div
          className="flex flex-col xl:h-full"
          style={
            isCompactTvViewport
              ? {
                  width: `${compactCanvasWidth}px`,
                  maxWidth: "none",
                  marginLeft: "50%",
                  transform: `translateX(-50%) scale(${compactScale})`,
                  transformOrigin: "top center",
                }
              : undefined
          }
        >
        <header className="mb-5 flex items-end justify-between gap-6 md:mb-1.5 xl:mb-3 2xl:mb-4">
          <img
            alt="КУБ Агентство недвижимости"
            className="h-auto w-[210px] md:w-[210px] xl:w-[200px] 2xl:w-[230px]"
            src={kybTvLogo}
          />

          <div className="hidden items-center gap-3 xl:flex">
            <span className="h-px w-16 bg-[linear-gradient(90deg,rgba(255,255,255,0.02),rgba(201,213,244,0.62),rgba(255,255,255,0.02))]" />
            <span className="text-[11px] uppercase tracking-[0.28em] text-[rgba(222,227,244,0.52)]">
              модели сотрудничества
            </span>
          </div>
        </header>

        {!hasSupabaseConfig && (
          <div className="mb-5 border-l border-[#8ea0d2] bg-black/10 px-4 py-3">
            <InfoBar
              className="m-0 p-0 [&_.info-bar_container]:m-0 [&_.info-bar_container]:p-0 [&_.info-bar_text]:text-[rgba(222,227,244,0.72)]"
              type="warning"
              text="Добавьте REACT_APP_SUPABASE_URL и REACT_APP_SUPABASE_ANON_KEY, чтобы страница начала читать данные из Supabase."
            />
          </div>
        )}

        {errorMessage && (
          <div className="mb-5 flex flex-col gap-[14px] border-l border-[#d2758d] bg-black/10 px-4 py-3">
            <InfoBar
              className="m-0 p-0 [&_.info-bar_container]:m-0 [&_.info-bar_container]:p-0 [&_.info-bar_text]:text-[#ffb9c8]"
              type="error"
              text={errorMessage}
            />
            <Button
              className="w-fit"
              iconLeft={<RefreshCw size={16} />}
              onClick={fetchModels}
              text="Повторить"
              variant="secondary"
            />
          </div>
        )}

        {!pending && hasSupabaseConfig && !errorMessage && !normalizedModels.length && (
          <div className="mb-5 border-l border-white/20 bg-black/10 px-5 py-8 text-left">
            <h2 className="m-0 text-[28px] text-[#f5f7ff]">
              Пока нет моделей сотрудничества
            </h2>
            <p className="m-0 mt-2 text-[15px] leading-[1.65] text-[rgba(222,227,244,0.72)]">
              Заполните записи в таблице `cooperation_models`, и они сразу
              появятся на этой странице.
            </p>
          </div>
        )}

        {pending ? (
          <section className="grid gap-4 md:grid-cols-3 md:gap-2.5 md:min-h-0 md:flex-1 xl:gap-4 2xl:gap-5">
            {Array.from({ length: 3 }).map((_, index) => {
              const tone = BRAND_TONES[index % BRAND_TONES.length];

              return (
                <article
                  className={[
                    "relative min-h-[420px] overflow-hidden rounded-[24px] px-5 py-4 md:min-h-0 md:rounded-[20px] md:px-3 md:py-2.5 xl:px-5 xl:py-4 2xl:px-6 2xl:py-5",
                    tone.surface,
                    "border border-white/10",
                  ].join(" ")}
                  key={index}
                >
                  <div className="mb-5 flex items-center justify-between md:mb-3">
                    <div className={["h-[2px] w-14 md:w-10", tone.line].join(" ")} />
                    <div className={["text-[88px] font-semibold leading-none tracking-[-0.08em] md:text-[72px] xl:text-[120px]", tone.number].join(" ")}>
                      0{index + 1}
                    </div>
                  </div>
                  <div className="relative mb-4 h-12 w-36 overflow-hidden bg-white/10 after:absolute after:inset-0 after:animate-[cooperation-models-loading_1.3s_infinite] after:bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.16),transparent)]" />
                  <div className="relative mb-6 h-3 w-24 overflow-hidden bg-white/10 after:absolute after:inset-0 after:animate-[cooperation-models-loading_1.3s_infinite] after:bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.16),transparent)]" />
                  <div>
                    {Array.from({ length: 5 }).map((__, lineIndex) => (
                      <div
                        className="relative mb-3 h-[14px] w-full overflow-hidden bg-white/10 after:absolute after:inset-0 after:animate-[cooperation-models-loading_1.3s_infinite] after:bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.16),transparent)]"
                        key={lineIndex}
                      />
                    ))}
                  </div>
                </article>
              );
            })}
          </section>
        ) : null}

        {!pending && normalizedModels.length > 0 && (
          <section className="grid gap-4 md:grid-cols-3 md:gap-2.5 md:min-h-0 md:flex-1 xl:gap-4 2xl:gap-5">
            {normalizedModels.map((model, index) => {
              const items = normalizeDescription(model.model_data.description);
              const visibleItems = items.slice(0, isCompactTvViewport ? 3 : 6);
              const tone = BRAND_TONES[index % BRAND_TONES.length];
              const isRichDescription = /<\/?[a-z][\s\S]*>/i.test(
                model.model_data.description,
              );

              return (
                <article
                  className={[
                    "relative flex min-h-[420px] flex-col overflow-hidden rounded-[24px] px-5 py-5 md:min-h-0 md:rounded-[20px] md:px-3 md:py-2.5 xl:px-5 xl:py-4 2xl:px-6 2xl:py-5",
                    tone.surface,
                    "border border-white/10 shadow-[0_24px_80px_rgba(2,6,20,0.28)]",
                  ].join(" ")}
                  key={model.id}
                  style={{ ["--model-accent" as string]: tone.accentHex }}
                >
                  <div className={["absolute right-3 top-2 text-[100px] font-semibold leading-none tracking-[-0.08em] md:text-[82px] xl:text-[138px]", tone.number].join(" ")}>
                    0{index + 1}
                  </div>

                  <div className="relative z-[1] mb-4 flex items-center justify-between gap-3 md:mb-1.5">
                    <div className={["h-[2px] w-14 md:w-10", tone.line].join(" ")} />
                    <span className="text-[10px] font-medium uppercase tracking-[0.24em] text-[rgba(222,227,244,0.38)] md:text-[8px]">
                      модель
                    </span>
                  </div>

                  <div className="relative z-[1] flex min-h-[216px] flex-col items-center text-center md:min-h-[112px] xl:min-h-[188px] 2xl:min-h-[208px]">
                    <h2 className="mb-3 font-['SF_Pro_Display','Segoe_UI',sans-serif] text-[clamp(52px,8vw,92px)] leading-[0.82] tracking-[-0.1em] text-[#f5f7ff] md:mb-1.5 md:text-[72px] xl:text-[clamp(48px,5vw,84px)] 2xl:text-[clamp(58px,4.2vw,96px)]">
                      {model.model_data.name}
                    </h2>
                    <p className="mb-4 max-w-[240px] text-[11px] uppercase leading-[1.4] tracking-[0.22em] text-[rgba(222,227,244,0.68)] md:mb-1.5 md:max-w-[170px] md:text-[9px] md:tracking-[0.16em] xl:text-[10px] 2xl:max-w-[280px] 2xl:text-[11px]">
                      Пакет сопровождения для брокера
                    </p>
                  </div>

                  <div className="relative z-[1] flex flex-1 flex-col justify-start md:overflow-hidden">
                    {isRichDescription ? (
                      <div
                        className={[
                          "flex max-w-[360px] flex-col text-[#f5f7ff] md:max-w-none",
                          "[&_p]:m-0 [&_p]:text-[15px] [&_p]:leading-[1.45] md:[&_p]:text-[11px] md:[&_p]:leading-[1.25] xl:[&_p]:text-[13px] 2xl:[&_p]:text-[15px]",
                          "[&_p+*]:mt-3 md:[&_p+*]:mt-1.5 [&_ul+*]:mt-3 md:[&_ul+*]:mt-1.5 [&_*+ul]:mt-3 md:[&_*+ul]:mt-1.5",
                          "[&_ul]:m-0 [&_ul]:w-full [&_ul]:list-none [&_ul]:space-y-2.5 [&_ul]:p-0 md:[&_ul]:space-y-1.5 2xl:[&_ul]:space-y-3",
                          "[&_li]:flex [&_li]:items-start [&_li]:gap-3 [&_li]:rounded-[14px] [&_li]:border [&_li]:border-white/10 [&_li]:bg-white/[0.035] [&_li]:px-3.5 [&_li]:py-2.5 [&_li]:text-left [&_li]:text-[16px] [&_li]:leading-[1.3] md:[&_li]:gap-1.5 md:[&_li]:rounded-[10px] md:[&_li]:px-2 md:[&_li]:py-1.5 md:[&_li]:text-[16px] xl:[&_li]:text-[13px] 2xl:[&_li]:px-4 2xl:[&_li]:py-3 2xl:[&_li]:text-[16px] [&_strong]:font-semibold",
                          "[&_li::before]:mt-[1px] [&_li::before]:block [&_li::before]:min-w-[22px] md:[&_li::before]:min-w-[12px] [&_li::before]:text-[11px] md:[&_li::before]:text-[7px] [&_li::before]:font-semibold [&_li::before]:tracking-[0.24em] md:[&_li::before]:tracking-[0.12em] [&_li::before]:text-[var(--model-accent)] [&_li::before]:content-['•']",
                          "[&_.ql-size-small]:text-[12px] [&_.ql-size-large]:text-[17px] [&_.ql-size-large]:leading-[1.2] 2xl:[&_.ql-size-large]:text-[20px]",
                        ].join(" ")}
                        dangerouslySetInnerHTML={{
                          __html: model.model_data.description,
                        }}
                      />
                    ) : items.length > 0 ? (
                      <ul className="flex max-w-[360px] list-none flex-col gap-2.5 p-0 md:max-w-none md:gap-1.5 2xl:max-w-[400px] 2xl:gap-3">
                        {visibleItems.map((item, itemIndex) => (
                          <li
                            className="flex items-start gap-3 rounded-[14px] border border-white/10 bg-white/[0.035] px-3.5 py-2.5 text-left text-[16px] leading-[1.3] text-[#f5f7ff] md:gap-1.5 md:rounded-[10px] md:px-2 md:py-1.5 md:text-[16px] xl:text-[13px] 2xl:px-4 2xl:py-3 2xl:text-[16px]"
                            key={item}
                          >
                            <span
                              className={[
                                "mt-[1px] inline-flex min-w-[20px] shrink-0 items-center justify-start text-[10px] font-semibold tracking-[0.22em] md:min-w-[12px] md:text-[7px] md:tracking-[0.12em] 2xl:min-w-[22px] 2xl:text-[11px]",
                                tone.accent,
                              ].join(" ")}
                            >
                              0{itemIndex + 1}
                            </span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="m-0 max-w-[320px] text-[15px] leading-[1.5] text-[rgba(222,227,244,0.78)] md:max-w-none md:text-[11px] md:leading-[1.25] xl:text-[13px] 2xl:max-w-[360px] 2xl:text-[15px]">
                        {model.model_data.description || "Описание пока не заполнено."}
                      </p>
                    )}
                  </div>
                </article>
              );
            })}
          </section>
        )}
        </div>
      </div>
    </div>
  );
};
